<template>
  <header
    :class="
      isCompact
        ? 'flex items-center justify-between min-h-[52px] px-3 pt-[calc(0.5rem+env(safe-area-inset-top,0px))] pb-2 bg-white'
        : 'flex items-center justify-between px-8 py-4 bg-white'
    "
  >
    <div class="flex items-center gap-4">
      <button
        v-if="isMobile"
        @click="showSideBar"
        class="text-gray-500 focus:outline-none lg:block"
      >
        <left-arrow v-if="isOpen" />
        <rigth-arrow v-else />
      </button>

      <div v-if="headerStore.title" class="flex flex-col text-left">
        <h1
          :class="
            isCompact ? 'text-lg font-bold text-textPrimary' : 'text-2xl font-bold text-textPrimary'
          "
        >
          <template v-if="headerStore.breadcrumbs.length > 0">
            <template v-for="(crumb, index) in headerStore.breadcrumbs" :key="index">
              <router-link :to="crumb.route" class="hover:text-primary">
                {{ crumb.label }}
              </router-link>
              <span class="mx-2">&rsaquo;</span>
            </template>
            <!-- Título dentro de <span>: whitespace entre elementos é removido pelo compilador
                 (modo condense). Como texto solto, o "\n" virava um espaço extra só no último nível,
                 deixando o espaçamento inconsistente entre os níveis do breadcrumb. -->
            <span aria-current="page">{{ headerStore.title }}</span>
          </template>
          <template v-else>
            {{ headerStore.title }}
          </template>
        </h1>
        <span v-if="headerStore.subtitle" class="text-sm text-[#163D45]">
          {{ headerStore.subtitle }}
        </span>
      </div>
    </div>

    <div class="flex items-center">
      <NotificationModal />

      <div class="relative">
        <button
          @click="dropdownOpen = !dropdownOpen"
          class="flex relative items-center"
          data-testid="header-user-avatar"
        >
          <v-avatar color="primary" class="mr-2">
            <ProtectedAvatar :src="avatarUrl" :size="40">
              <span class="text-md font-bold">{{ getInitalsName(userName) }}</span>
            </ProtectedAvatar>
          </v-avatar>

          <v-icon
            :icon="mdiChevronDown"
            size="20"
            color="primary"
            :class="['transition-transform duration-200', { 'rotate-180': dropdownOpen }]"
          />
        </button>

        <div
          v-show="dropdownOpen"
          @click="dropdownOpen = false"
          class="fixed inset-0 z-10 w-full h-full"
        ></div>
        <transition
          enter-active-class="transition duration-150 ease-out transform"
          enter-from-class="scale-95 opacity-0"
          enter-to-class="scale-100 opacity-100"
          leave-active-class="transition duration-150 ease-in transform"
          leave-from-class="scale-100 opacity-100"
          leave-to-class="scale-95 opacity-0"
        >
          <div
            v-show="dropdownOpen"
            class="absolute right-0 z-20 w-72 mt-2 bg-white rounded-xl shadow-md border border-gray-200/80 overflow-hidden"
          >
            <div class="px-4 py-4 bg-gray-50/80 border-b border-gray-200">
              <div class="flex items-center gap-3">
                <v-avatar color="primary" size="42">
                  <ProtectedAvatar :src="avatarUrl" :size="42">
                    <span class="text-sm font-bold">{{ getInitalsName(userName) }}</span>
                  </ProtectedAvatar>
                </v-avatar>
                <div class="min-w-0 text-left">
                  <p class="text-sm font-semibold text-gray-900 truncate">{{ userName }}</p>
                  <p class="text-xs text-gray-500 truncate mt-0.5">{{ userEmail }}</p>
                </div>
              </div>
            </div>

            <router-link
              to="/perfil"
              data-testid="header-edit-profile-link"
              class="group flex items-center gap-3 px-4 py-3.5 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors duration-150"
              @click="dropdownOpen = false"
            >
              <v-icon
                :icon="mdiAccountOutline"
                size="20"
                class="text-gray-400 group-hover:text-primary transition-colors duration-150"
              />
              Editar Perfil
            </router-link>

            <a
              :href="SUPPORT_CHAT_URL"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-support-link"
              class="group flex items-center gap-3 px-4 py-3.5 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors duration-150"
            >
              <v-icon
                :icon="mdiHelpCircleOutline"
                size="20"
                class="text-gray-400 group-hover:text-primary transition-colors duration-150"
              />
              Suporte
            </a>

            <div class="border-t border-gray-100" />

            <button
              @click="logout"
              data-testid="header-logout-btn"
              class="group flex w-full items-center gap-3 px-4 py-3.5 text-sm font-medium text-red-500 hover:bg-red-50 hover:text-red-600 transition-colors duration-150"
            >
              <v-icon
                :icon="mdiLogout"
                size="20"
                class="text-red-400 group-hover:text-red-600 transition-colors duration-150"
              />
              Sair
            </button>
          </div>
        </transition>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { mdiChevronDown, mdiAccountOutline, mdiHelpCircleOutline, mdiLogout } from '@mdi/js'
import LeftArrow from '@/assets/icons/rightArrow.vue'
import RigthArrow from '@/assets/icons/leftArrow.vue'
import { useCompanyStore } from '@/pinia-store/useCompanyStore'
import { useAuthStore } from '@/pinia-store/useAuthStore'
import { useUserStore } from '@/pinia-store/useUsersStore'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'
import { useNotificationStore } from '@/pinia-store/useNotificationStore'
import { resolveAvatarSrc } from '@/utils/avatar'
import { SUPPORT_CHAT_URL } from '@/config/support'
import { usePushNotifications } from '@/hooks/usePushNotifications'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useShellIsMobile } from '@/hooks/useShellIsMobile'
import { useSidebar } from '../hooks/useSidebar'
import NotificationModal from './others/NotificationModal.vue'
import ProtectedAvatar from '@/components/designSystem/AvatarUser/ProtectedAvatar.vue'

const companyStore = useCompanyStore()
const authStore = useAuthStore()
const userStore = useUserStore()
const headerStore = useHeaderStore()
const { myCompanies } = storeToRefs(useCompanyStore())

const dropdownOpen = ref(false)

const { isOpen, setIsOpen } = useSidebar()
const isMobile = useShellIsMobile()
// Barra de 52px (☰, título, avatar) só com a flag do celular; sem ela as classes do Header são
// exatamente as de sempre (mesma ordem). No compacto, o padding-top arbitrário com calc soma a
// safe area do topo (notch) aos 8px; pt/pb no lugar de py-2 porque o Tailwind usa
// `important: '#app'` e qualquer CSS próprio perderia para `#app .py-2`.
const { flags } = useFeatureFlags()
const isCompact = computed(() => flags.enabledDashboardMobile && isMobile.value)
const { initialize: initPush } = usePushNotifications()
const notificationStore = useNotificationStore()

function showSideBar() {
  setIsOpen(true)
}

onMounted(async () => {
  await userStore.getUserData()

  await companyStore.getMyCompanies()
  myCompanies.value.forEach(({ id }) => {
    companyStore.setCompanyIdsSelected({ checked: true, id })
  })

  initPush()
  notificationStore.startPolling()
})

onUnmounted(() => {
  notificationStore.stopPolling()
})

const userName = computed(() => {
  return userStore.userData?.name || 'Usuário'
})

const userEmail = computed(() => {
  return userStore.userData?.email || ''
})

const avatarUrl = computed(() => resolveAvatarSrc(userStore.userData?.avatarUrl))

const getInitalsName = (name: string) => {
  return name.substring(0, 2).toUpperCase()
}

const logout = () => authStore.logout()
</script>
